import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { formatNumber } from "@/lib/formatters";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

interface VideoMetrics {
  videoViews: number;
  videoP25: number;
  videoP50: number;
  videoP75: number;
  videoP100: number;
  sixSecondViews: number;
  avgWatchTime: number;
  impressions: number;
}

export function VideoPerformanceChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<VideoMetrics>({
    queryKey: ["video-metrics", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("video-metrics", params));
      if (!res.ok) throw new Error("Failed to fetch video metrics");
      return res.json();
    },
    enabled: sourceType === "unified",
  });

  const funnelData = data ? [
    { stage: "25% Viewed", value: data.videoP25, percent: 100 },
    { stage: "50% Viewed", value: data.videoP50, percent: data.videoP25 > 0 ? Math.round((data.videoP50 / data.videoP25) * 100) : 0 },
    { stage: "75% Viewed", value: data.videoP75, percent: data.videoP25 > 0 ? Math.round((data.videoP75 / data.videoP25) * 100) : 0 },
    { stage: "100% Viewed", value: data.videoP100, percent: data.videoP25 > 0 ? Math.round((data.videoP100 / data.videoP25) * 100) : 0 },
  ] : [];

  const avgWatchTime = data?.avgWatchTime ?? 0;
  const sixSecViewRate = data && data.impressions > 0
    ? Math.round((data.sixSecondViews / data.impressions) * 10000) / 100
    : 0;

  const barColors = ["#6366F1", "#818CF8", "#A5B4FC", "#C7D2FE"];

  return (
    <DashboardWidget
      title="Video View Funnel"
      widgetType="chart"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="p-4 h-full flex flex-col" data-testid="chart-video-performance">
        {!data || data.videoViews === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "#5F6D82" }}>
            No video data available
          </div>
        ) : (
          <>
            <div className="flex gap-4 mb-3 text-xs">
              <div className="bg-indigo-50 px-3 py-1.5 rounded-lg">
                <span className="text-gray-500">Avg Watch Time</span>
                <span className="ml-2 font-semibold text-gray-900" data-testid="avg-watch-time">{avgWatchTime}s</span>
              </div>
              <div className="bg-indigo-50 px-3 py-1.5 rounded-lg">
                <span className="text-gray-500">6s View Rate</span>
                <span className="ml-2 font-semibold text-gray-900" data-testid="six-sec-view-rate">{sixSecViewRate}%</span>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} layout="vertical" barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => formatNumber(v)} />
                  <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: "#6B7280" }} width={90} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(value: number) => [formatNumber(value), "Views"]}
                  />
                  <Bar dataKey="value" name="Views" radius={[0, 4, 4, 0]}>
                    {funnelData.map((_, idx) => (
                      <Cell key={idx} fill={barColors[idx]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </DashboardWidget>
  );
}
